import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { DirectSubscriptionService } from '@app/modules/direct/services/direct-subscription.service';
import { PropertyUpdateStatusComponent } from '@app/features/properties';
import { TaxSelectionComponent, PropertyPricingService } from '@app/features/pricing-and-availability';
import { DEFAULT_CALCULATION_TYPE, PricesAndFees, Tax } from '@app/shared/models/tax';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { Property } from '@app/shared/services/properties/properties.service';
import { TaxService } from '@app/shared/services/taxes/tax.service';
import { TranslocoRootModule } from '@app/transloco-root.module';
import { UiModule } from '@app/ui/ui.module';
import { combineLatest, of } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { CurrencyCodePipe } from '@app/shared/pipes/currency-code/currency-code.pipe';
import { PropertyUpdateStatusByPlatform } from '@app/shared/interfaces/lib/property.interface';
@Component({
  standalone: true,
  selector: 'sbnb-property-fees-taxes-discounts',
  templateUrl: './property-fees-taxes-discounts.component.html',
  styleUrls: ['./property-fees-taxes-discounts.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    UiModule,
    TranslocoRootModule,
    PropertyUpdateStatusComponent,
    TaxSelectionComponent,
    CurrencyCodePipe,
  ],
})
export class PropertyFeesTaxesDiscountsComponent implements OnInit {
  @Input() property: Property;
  @Input() section: 'security-deposit' | 'occupancy-based' | 'fees' | 'discounts';

  public fees = PricesAndFees.fees;
  public discounts = PricesAndFees.discounts;
  public occupancyBased = PricesAndFees.occupancyBased;
  enableTaxes = false;
  taxes: Tax[];
  taxesUuids: string[];
  public loading$ = this.propertyDetailService.loadingPricingFeeDiscount$;
  public priceSaveState$ = this.propertyDetailService.priceSaveState$;
  public featureFlagAirbnbFeesEnabled$ = this.posthogService.airbnbFeesEnabled$;
  public featureFlagAirbnbDiscountsEnabled$ = this.posthogService.airbnbDiscountsEnabled$;
  public featureFlagDirectSecurityDepositsEnabled$ = this.posthogService.directSecurityDepositsEnabled$;

  public pricingFeeDiscountData$ = this.propertyDetailService.pricingFeeDiscountData$.pipe(
    map((data) => {
      // Set the calculation type to per-reservation for fees that have a value but no calculation type
      const fees = ['cleaning_fee', 'resort_fee', 'community_fee', 'linens_fee', 'management_fee', 'pet_fee'];
      for (const fee of fees) {
        if (data[fee].value === null) {
          data[fee] = {
            value: 0,
            type: 'fixed',
            calculation_type: DEFAULT_CALCULATION_TYPE,
          };
        }
      }

      // Transform early bird discount to array if it's not already
      if (data.early_bird_discount && !Array.isArray(data.early_bird_discount)) {
        data.early_bird_discount = [];
      }

      if (data.last_minute_discount && !Array.isArray(data.last_minute_discount)) {
        data.last_minute_discount = [];
      }

      return {
        ...data,
        early_bird_discount: data.early_bird_discount || [],
        last_minute_discount: data.last_minute_discount || [],
        maximum_occupancy: {
          // Append maximum occupancy
          type: 'fixed',
          value: this.property.person_capacity,
          edited: false,
        },
      };
    })
  );

  objectKeys = Object.keys;

  public feeUpdates: { [key: string]: { value?: any; calculation_type?: string } } = {};
  public showIconLegend = false;
  public status = PropertyUpdateStatusByPlatform;

  constructor(
    private taxService: TaxService,
    public authService: AuthenticationService,
    private directSubscription: DirectSubscriptionService,
    private permissionsService: UserPermissionsService,
    private readonly propertyDetailService: PropertyPricingService,
    private posthogService: PosthogService
  ) {}

  ngOnInit(): void {
    this.enableTaxesCheck();
  }

  getTaxes() {
    this.taxService.getTaxes().subscribe((taxes) => {
      this.taxes = taxes.filter((tax) => tax.properties.includes(this.property.id));
      this.taxesUuids = this.taxes.map((tax) => tax.uuid);
    });
  }

  enableTaxesCheck() {
    combineLatest([this.directSubscription.isBasic$, this.permissionsService.isPrimaryUser()])
      .pipe(first(([isBasic, isPrimary]: [boolean, boolean]) => isBasic && isPrimary))
      .subscribe(([isBasic, isPrimary]: [boolean, boolean]) => {
        this.enableTaxes = true;
        this.getTaxes();
      });
  }

  savePriceChange(key: string, dataToUpdate: any) {
    this.propertyDetailService.savePriceChange(key, dataToUpdate);
  }

  // Store fees & update calculation type - but not submit them
  // TODO: probably we can just have an array of keys here of what has been touched, currentState is in practice pricingFeeDiscountData$ value
  storeFeeUpdate(key: string, currentState: any, type: 'value' | 'calculationType') {
    // Initialize the key if it doesn't exist
    if (!this.feeUpdates[key]) {
      this.feeUpdates[key] = { ...currentState };
    }

    if (type === 'value') {
      this.feeUpdates[key].value = currentState.value;
    } else if (type === 'calculationType') {
      this.feeUpdates[key].calculation_type = currentState.calculation_type;

      // Get the fee configuration for this key
      const fee = this.fees.find((f) => f.key === key);
      if (fee) {
        // Find the calculation type configuration
        const calcType = fee.calculationTypes?.find((ct) => ct.value === currentState.calculation_type);
        if (calcType) {
          // If the current type (fixed/percent) isn't available in the new calculation type,
          // switch to the available one
          const currentType = currentState.type;
          if (currentType === 'fixed' && !calcType.fixed) {
            currentState.type = 'percent';
            if (!this.feeUpdates[key].value) {
              this.feeUpdates[key].value = currentState[key];
            }
          } else if (currentType === 'percent' && !calcType.percent) {
            currentState[key].type = 'fixed';
            if (!this.feeUpdates[key].value) {
              this.feeUpdates[key].value = currentState[key];
            }
          }
        }
      }
    }
  }

  getCalculationOptionBasedOnKey(key: string, option: string) {
    return this.fees.find((fee) => fee.key === key)?.calculationTypes?.find((ct) => ct.value === option);
  }

  batchSave() {
    const changes = Object.entries(this.feeUpdates).map(([key, data]) => {
      return { key, data };
    });

    this.propertyDetailService.savePriceChanges(changes);

    this.feeUpdates = {};
  }

  canUseType(fee: any, calculationType: string, type: 'fixed' | 'percent'): boolean {
    const calcType = fee.calculationTypes?.find((ct) => ct.value === calculationType);
    return type === 'fixed' ? calcType?.fixed : calcType?.percentage;
  }

  invlidateIncompatibleCalculationType(key: string, currentState: any, type: 'fixed' | 'percent') {
    const calcType = this.fees
      .find((fee) => fee.key === key)
      ?.calculationTypes?.find((ct) => ct.value === currentState.calculation_type);
    if (!calcType?.[currentState.type]) {
      currentState.calculation_type = 'per-reservation';
    }
  }

  /* Discount helper methods | Airbnb update */

  /* Add new discount, locally only */
  addDiscount(currentData: any, key: string) {
    if (!currentData[key]) {
      currentData[key] = [];
    }

    currentData[key].push({
      type: 'percent',
      value: 0,
      threshold_days: 1,
      local: true,
    });
  }

  /* Remove discount, send a request only if it is not marked as local only */
  removeDiscount(key: string, data: any[], index: number) {
    const removed = data.splice(index, 1);
    if (!removed?.[0]?.local) {
      this.checkAndSave(key, data);
    }
  }

  /* OnChanges, check if we should submit a request, if there is an error the current field will be highlighted */
  // TODO: when in forms, all fields will be checked & highlighted
  checkAndSave(key: string, data: any[]) {
    const submitted = [];
    for (const item of data) {
      if (!this.validateAirbnbDiscounts(key, item)) return;
      if (item.local) {
        if (item.value > 0 && item.threshold_days > 0) {
          delete item.local;
          submitted.push(item);
        }
      } else {
        if (!item.value || !item.threshold_days) {
          // error case, do nothing, ui will highlight the error field
          return;
        }
        submitted.push(item);
      }
    }

    const payload = submitted.map((item) => {
      if ('edited' in item) {
        delete item.edited;
      }
      return item;
    });

    this.savePriceChange(key, payload);
  }

  validateAirbnbDiscounts(key: string, data: { threshold_days: number; value: number }) {
    if (key === 'last_minute_discount') {
      if (data.threshold_days < 0 || data.threshold_days > 28 || data.value <= 0 || data.value > 100) {
        return false;
      }
    }

    if (key === 'early_bird_discount') {
      if (data.threshold_days < 0 || data.threshold_days > 24 || data.value <= 0 || data.value > 100) {
        return false;
      }
    }
    return true;
  }
}
